import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * HotelOwnerRouteGuard
 * Handles routing logic for hotel owners based on their registration status
 * 
 * Status-based access:
 * - NOT REGISTERED → Can only access: /owner/register
 * - PENDING → Can access: /owner/status, /owner/register, /owner/settings
 * - DECLINED → Can access: /owner/status, /owner/register, /owner/settings
 * - APPROVED → Can access: All owner pages including dashboard
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components to render
 * @param {boolean} props.requireApproval - If true, only approved owners can access
 */
const HotelOwnerRouteGuard = ({ children, requireApproval = false }) => {
  const { currentUser, userProfile, hotelStatus, hotelStatusLoading } = useAuth();
  const location = useLocation();

  if (hotelStatusLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <div style={{ 
          background: 'white', 
          padding: '40px', 
          borderRadius: '12px',
          textAlign: 'center'
        }}>
          <div style={{
            width: '50px',
            height: '50px',
            border: '4px solid #e5e7eb',
            borderTopColor: '#667eea',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }}></div>
          <div>Loading...</div>
        </div>
      </div>
    );
  }

  const currentPath = location.pathname;
  const status = hotelStatus?.status;

  // Define allowed paths based on status
  const allowedPaths = {
    not_registered: ['/owner', '/owner/status', '/owner/register', '/owner/settings'],
    pending: ['/owner', '/owner/status', '/owner/register', '/owner/settings', '/owner/properties'],
    declined: ['/owner', '/owner/status', '/owner/register', '/owner/settings', '/owner/properties'],
    approved: [
      '/owner',
      '/owner/status',
      '/owner/register', 
      '/owner/settings',
      '/owner/properties',
      '/owner/bookings',
      '/owner/rooms',
      '/owner/staff',
      '/owner/maintenance',
      '/owner/reviews',
      '/owner/reports',
      '/owner/analytics'
    ]
  };

  // Check if current path is allowed for the user's status
  const isPathAllowed = (path, userStatus) => {
    const allowed = allowedPaths[userStatus] || [];
    return allowed.some(allowedPath => 
      path === allowedPath || path.startsWith(allowedPath + '/')
    );
  };

  // Routing logic based on status
  if (status === 'not_registered') {
  // Allow /owner, /owner/register, /owner/status, /owner/settings
    if (!isPathAllowed(currentPath, status)) {
    return <Navigate to="/owner/register" replace />;
    }
  }

  if (status === 'pending' || status === 'declined') {
    // For paths other than /owner root, check if allowed
    // Don't redirect /owner itself - let HotelOwnerDashboard component handle display
    if (currentPath !== '/owner' && !isPathAllowed(currentPath, status)) {
      // Default to status page for pending/declined users on disallowed paths
      return <Navigate to="/owner/status" replace />;
    }
  }

  if (status === 'approved') {
    // Approved owners can access everything
    // No special handling needed - just allow access
  }

  // If requireApproval flag is set, only approved users can access
  if (requireApproval && status !== 'approved') {
    return <Navigate to="/owner/status" replace />;
  }

  // Allow access to the requested page
  return children;
};

export default HotelOwnerRouteGuard;
